import { includes, isEmpty, isUndefined } from 'lodash';
import { AccountConstant } from 'src/constants';
import { RetirementAccountTypeDto } from 'src/dtos';
import { Account, AccountType } from 'src/models';
import { getLocalStorageService } from 'src/services';

import { Maybe } from './types.utils';

const isPrimaryPersonalInformationComplete = (account?: Account) =>
  !isUndefined(account?.primaryAccountHolder?.dateOfBirth);

const isPrimaryPhysicalAddressComplete = (account?: Account) =>
  !isEmpty(account?.primaryAccountHolder?.physicalAddress);

const isPrimaryMailingAddressComplete = (account?: Account) => !isEmpty(account?.primaryAccountHolder?.mailingAddress);

const isJointPersonalInformationComplete = (account?: Account) =>
  !isUndefined(account?.secondaryAccountHolder?.dateOfBirth);

const isJointPhysicalAddressComplete = (account?: Account) =>
  !isEmpty(account?.secondaryAccountHolder?.physicalAddress);

const isJointMailingAddressComplete = (account?: Account) => !isEmpty(account?.secondaryAccountHolder?.mailingAddress);

const isPrimaryAccountHolderPersonalInformationComplete = (account?: Account) =>
  isPrimaryPersonalInformationComplete(account) &&
  isPrimaryPhysicalAddressComplete(account) &&
  isPrimaryMailingAddressComplete(account);

const isJointAccountHolderPersonalInformationComplete = (account?: Account) =>
  isJointPersonalInformationComplete(account) &&
  isJointPhysicalAddressComplete(account) &&
  isJointMailingAddressComplete(account);

export const isAccountPersonalInformationComplete = (account?: Account) => {
  if (account?.type.isJoint || account?.type.isUgma) {
    return (
      isPrimaryAccountHolderPersonalInformationComplete(account) &&
      isJointAccountHolderPersonalInformationComplete(account)
    );
  }

  return isPrimaryAccountHolderPersonalInformationComplete(account);
};

export const isAccountTrustedContactComplete = (account?: Account) => {
  if (!account) {
    return false;
  }

  if (account?.type.isJoint && account?.jointAccountType?.isTenantsInCommon) {
    return (
      !isUndefined(account?.primaryAccountHolder?.jointTenantsInCommonInterestPercent) &&
      !isUndefined(account?.secondaryAccountHolder?.jointTenantsInCommonInterestPercent)
    );
  }

  return true;
};

export const isAccountSuitabilityInformationComplete = (account?: Account) => {
  if (!account) {
    return false;
  }

  return !isEmpty(account?.suitabilityInformation);
};

const isAssetsInformationComplete = (account?: Account) => !isUndefined(account?.financialAssets?.annualIncome);

const isPrimaryEmploymentComplete = (account?: Account) =>
  !isUndefined(account?.primaryAccountHolder?.financialInformation?.employmentStatus);

const isJointEmploymentComplete = (account?: Account) =>
  !isUndefined(account?.secondaryAccountHolder?.financialInformation?.employmentStatus);

export const isAccountFinancialInformationComplete = (account?: Account) => {
  if (account?.type.isJoint || account?.type.isUgma) {
    return (
      isAssetsInformationComplete(account) && isPrimaryEmploymentComplete(account) && isJointEmploymentComplete(account)
    );
  }

  return isAssetsInformationComplete(account) && isPrimaryEmploymentComplete(account);
};

const isPrimaryDisclosureComplete = (account?: Account) =>
  !isUndefined(account?.primaryAccountHolder?.disclosure?.statementDelivery?.value);

const isJointDisclosureComplete = (account?: Account) =>
  !isUndefined(account?.secondaryAccountHolder?.disclosure?.statementDelivery?.value);

export const isAccountDisclosureComplete = (account?: Account) => {
  if (account?.type.isJoint) {
    return isPrimaryDisclosureComplete(account) && isJointDisclosureComplete(account);
  }

  return isPrimaryDisclosureComplete(account);
};

export const isAccountAgreementsComplete = (account?: Account) => {
  return 'true' === getLocalStorageService().find(`${AccountConstant.SIGN_APPLICATION_PREFIX_KEY}-${account?.id}`);
};

export const isAccountComplete = (account?: Account) => {
  if (!account) {
    return false;
  }

  return (
    isAccountPersonalInformationComplete(account) &&
    isAccountTrustedContactComplete(account) &&
    isAccountSuitabilityInformationComplete(account) &&
    isAccountFinancialInformationComplete(account) &&
    isAccountDisclosureComplete(account) &&
    isAccountAgreementsComplete(account)
  );
};

export const getAccountFundingInstructions = (account?: Account) => {
  if (!account?.accountNumber || !account?.primaryAccountHolder) {
    return `My IPO Account Number, Account Holder’s Name`;
  }

  const { firstName, middleName, lastName, suffix } = account.primaryAccountHolder;

  if (account.type.isJoint && account.secondaryAccountHolder) {
    const {
      firstName: jointFirstName,
      lastName: jointLastName,
      middleName: jointMiddleName,
      suffix: jointSuffix,
    } = account.secondaryAccountHolder;

    if (lastName === jointLastName) {
      return `${account?.accountNumber}, ${lastName}, ${[firstName, middleName, suffix]
        .filter(Boolean)
        .join(' ')} and ${[jointFirstName, jointMiddleName, jointSuffix].filter(Boolean).join(' ')}`;
    }

    return `${account?.accountNumber}, ${lastName}/${jointLastName}, ${[firstName, middleName, suffix]
      .filter(Boolean)
      .join(' ')} and ${[jointFirstName, jointMiddleName, jointSuffix].filter(Boolean).join(' ')}`;
  }

  let fullName = [firstName, middleName, lastName, suffix].filter(Boolean).join(' ');

  if (account.type.isRetirement) {
    fullName = fullName.concat(account.retirementAccountType?.label ? ` ${account.retirementAccountType?.label}` : '');
  }

  return `${account?.accountNumber}, ${fullName}`;
};

export const isRetirementAccountFundingAllowed = (retirementAccountType?: RetirementAccountTypeDto) =>
  includes(
    [
      RetirementAccountTypeDto.Traditional,
      RetirementAccountTypeDto.Simple,
      RetirementAccountTypeDto.SEP,
      RetirementAccountTypeDto.Roth,
      RetirementAccountTypeDto.Rollover,
      RetirementAccountTypeDto.CoverDell,
    ],
    retirementAccountType,
  );

export const getInvestorName = (account?: Account) => {
  if (!account?.primaryAccountHolder) {
    return;
  }
  const { firstName, middleName, lastName, suffix } = account.primaryAccountHolder;
  const fullName = [firstName, middleName, lastName, suffix].filter(Boolean).join(' ');

  if (account?.type.isRetirement) {
    return fullName.concat(account.retirementAccountType?.label ? ` ${account.retirementAccountType?.label}` : '');
  }

  if (account?.type.isUgma && account?.secondaryAccountHolder) {
    const {
      firstName: jointFirstName,
      middleName: jointMiddleName,
      lastName: jointLastName,
      suffix: jointSuffix,
    } = account?.secondaryAccountHolder;
    const jointFullName = [jointFirstName, jointMiddleName, jointLastName, jointSuffix].filter(Boolean).join(' ');

    return `${fullName} C/F ${jointFullName}`;
  }

  if (account?.type.isJoint && account?.secondaryAccountHolder) {
    const {
      firstName: jointFirstName,
      middleName: jointMiddleName,
      lastName: jointLastName,
      suffix: jointSuffix,
    } = account?.secondaryAccountHolder;
    const jointFullName = [jointFirstName, jointMiddleName, jointLastName, jointSuffix].filter(Boolean).join(' ');

    return `${fullName} and ${jointFullName}`;
  }

  return fullName;
};

export const getAccountHolderSubheader = ({
  type,
  isPrimary = false,
  authorizedSigner,
}: {
  type?: AccountType;
  isPrimary?: boolean;
  authorizedSigner?: number;
}): Maybe<string> => {
  if (isPrimary && (type?.isJoint || type?.isUgma)) {
    return 'Primary Account Holder';
  }

  if (type?.isJoint) {
    return 'Joint Account Holder';
  }

  if (type?.isUgma) {
    return 'UGMA Beneficiary';
  }

  if (isPrimary && type?.isEntity) {
    return 'Authorized Signer 1';
  }

  if (type?.isEntity && authorizedSigner) {
    return `Authorized Signer ${authorizedSigner}`;
  }
};

export const shouldDisplayAccountHolderInformation = ({
  type,
  isEntityAccountTypeActive,
}: {
  type?: AccountType;
  isEntityAccountTypeActive?: boolean;
}) => {
  if (type?.isEntity && !isEntityAccountTypeActive) {
    return false;
  }

  return true;
};

export const shouldDisplayJointAccountHolderInformation = ({
  type,
  isEntityAccountTypeActive,
}: {
  type?: AccountType;
  isEntityAccountTypeActive?: boolean;
}) => {
  if (type?.isJoint || type?.isUgma) {
    return true;
  }

  if (isEntityAccountTypeActive) {
    return true;
  }

  return false;
};
